/**
 * Ratings and reviews.
 *
 * A summary carries the average, the count, a five-bucket distribution and a
 * few recent public reviews. The distribution is a fixed five-element array
 * where index 0 is one star; rendering it backwards is the obvious bug and the
 * type is written to make the ordering explicit at the call site.
 */

import { api, LIST_ID } from "../api";
import type {
    CreateRatingRequest,
    Meta,
    Rating,
    RatingSummary,
    UUID,
} from "../types";
import type { EnvelopeMeta } from "../baseQuery";

export interface RatingPage {
    items: Rating[];
    meta?: Meta;
}

/** A complaint about one review, and what the API did with it. */
export interface ReportRatingRequest {
    ratingId: UUID;
    /** Required, and at most 1000 characters. An empty one is refused. */
    reason: string;
}

/**
 * The outcome of reporting a review.
 *
 * `created` is the difference between 201 and 200, and it is not a detail:
 * "thank you, we have it" and "you have already reported this" are different
 * things to tell somebody, and the second is not an error. Flattening the two
 * into a void response would throw away the only signal that tells them apart.
 */
export interface RatingReport {
    created: boolean;
}

export const ratingsApi = api.injectEndpoints({
    endpoints: (build) => ({
        submitRating: build.mutation<Rating, CreateRatingRequest>({
            query: (body) => ({ url: "ratings", method: "POST", body }),
            invalidatesTags: (_r, _e, arg) => [
                { type: "Rating", id: LIST_ID },
                { type: "Rating", id: arg.rideId },
                { type: "RatingSummary", id: arg.rateeId },
                { type: "Ride", id: arg.rideId },
            ],
        }),

        /** Ratings the caller has given. */
        myRatings: build.query<RatingPage, { page?: number; limit?: number } | void>({
            query: (args) => ({ url: "ratings/mine", params: args ?? {} }),
            transformResponse: (rows: Rating[] | null, meta: EnvelopeMeta) => ({
                items: rows ?? [],
                meta: meta?.envelope?.meta,
            }),
            providesTags: [{ type: "Rating", id: LIST_ID }],
        }),

        /** The caller's own rating for a ride, if they have left one. */
        ratingForRide: build.query<Rating | null, UUID>({
            query: (rideId) => `ratings/rides/${rideId}`,
            providesTags: (_r, _e, rideId) => [{ type: "Rating", id: rideId }],
        }),

        /** A person's public reputation. Safe to show to anyone. */
        ratingSummary: build.query<RatingSummary, UUID>({
            query: (userId) => `ratings/users/${userId}`,
            providesTags: (_r, _e, userId) => [
                { type: "RatingSummary", id: userId },
            ],
        }),

        /**
         * Reports a review to the moderation queue.
         *
         * Open to any signed-in user, not only the person the review is about:
         * whoever notices abuse in a review is usually not its subject. The
         * one refusal that is about identity is reporting your own review,
         * which the API answers with a 403.
         *
         * Invalidates nothing. A report changes what an operator sees in the
         * moderation queue and nothing at all in this client's caches: the
         * review is still there, still says the same thing, and hiding it is a
         * decision somebody else makes later.
         */
        reportRating: build.mutation<RatingReport, ReportRatingRequest>({
            query: ({ ratingId, reason }) => ({
                url: `ratings/${ratingId}/report`,
                method: "POST",
                body: { reason },
            }),
            // The status is the only place the two outcomes differ in kind, so
            // it is read here rather than left for a caller to dig out of meta.
            transformResponse: (_body: unknown, meta: EnvelopeMeta) => ({
                created: meta?.response?.status === 201,
            }),
        }),
    }),
});

export const {
    useSubmitRatingMutation,
    useMyRatingsQuery,
    useRatingForRideQuery,
    useRatingSummaryQuery,
    useReportRatingMutation,
} = ratingsApi;
